import React, { useEffect, useState, Component } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon, EyeIcon, CheckCircleIcon, XCircleIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { useSiteContent } from '../../contexts/SiteContentContext';
// Simple Toast Component
const Toast = ({
  message,
  type,
  onClose
}: {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return <div className={`fixed top-20 right-8 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white animate-fadeIn ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`} data-id="element-1996">
      {type === 'success' ? <CheckCircleIcon size={20} data-id="element-1997" /> : <AlertCircleIcon size={20} data-id="element-1998" />}
      <span className="font-medium text-sm" data-id="element-1999">{message}</span>
      <button onClick={onClose} className="ml-4 hover:opacity-80" data-id="element-2000">
        <XIcon size={16} data-id="element-2001" />
      </button>
    </div>;
};
const ContentManager = () => {
  const {
    articles,
    addArticle,
    updateArticle,
    deleteArticle,
    deleteArticles
  } = useSiteContent();
  const [activeTab, setActiveTab] = useState('news');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [currentArticle, setCurrentArticle] = useState<any>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'announcements',
    image: '',
    tags: '',
    featured: false,
    author: 'Admin',
    date: new Date().toISOString().split('T')[0],
    status: 'published'
  });
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
  };
  // Filtering
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || article.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedItems(paginatedArticles.map(a => a.id));
    } else {
      setSelectedItems([]);
    }
  };
  const handleSelectItem = (id: string) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  const handleBulkAction = (action: 'delete' | 'publish' | 'draft') => {
    if (selectedItems.length === 0) return;
    if (action === 'delete') {
      deleteArticles(selectedItems);
      showToast(`${selectedItems.length} items deleted from website`);
    } else {
      const newStatus = action === 'publish' ? 'published' : 'draft';
      selectedItems.forEach(id => updateArticle(id, {
        status: newStatus
      }));
      showToast(`${selectedItems.length} items ${newStatus} on website`);
    }
    setSelectedItems([]);
  };
  const handleStatusToggle = (id: string) => {
    const article = articles.find(a => a.id === id);
    if (article) {
      const newStatus = article.status === 'published' ? 'draft' : 'published';
      updateArticle(id, {
        status: newStatus
      });
      showToast(`Article ${newStatus === 'published' ? 'published to' : 'removed from'} live website`);
    }
  };
  const openCreateModal = () => {
    setFormData({
      id: '',
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'announcements',
      image: '',
      tags: '',
      featured: false,
      author: 'Admin',
      date: new Date().toISOString().split('T')[0],
      status: 'published'
    });
    setIsModalOpen(true);
  };
  const openEditModal = (article: any) => {
    setFormData({
      ...article,
      tags: Array.isArray(article.tags) ? article.tags.join(', ') : article.tags || ''
    });
    setIsModalOpen(true);
  };
  const openDeleteModal = (article: any) => {
    setCurrentArticle(article);
    setIsDeleteModalOpen(true);
  };
  const openPreviewModal = (article: any) => {
    setCurrentArticle(article);
    setIsPreviewModalOpen(true);
  };
  const handleDelete = () => {
    if (currentArticle) {
      deleteArticle(currentArticle.id);
      showToast('Article deleted from website');
      setIsDeleteModalOpen(false);
      setCurrentArticle(null);
    }
  };
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      showToast('Title and content are required', 'error');
      return;
    }
    const articleData = {
      ...formData,
      id: formData.id || Date.now().toString(),
      slug: formData.slug || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''),
      tags: formData.tags.split(',').map((t: string) => t.trim()).filter(Boolean)
    };
    if (formData.id) {
      updateArticle(formData.id, articleData);
      showToast('Article updated — changes are live on website');
    } else {
      addArticle(articleData as any);
      showToast('Article published to live website');
    }
    setIsModalOpen(false);
  };
  const tabsList = [{
    id: 'news',
    label: 'News & Articles'
  }, {
    id: 'events',
    label: 'Events'
  }, {
    id: 'pages',
    label: 'Static Pages'
  }];
  return <div className="space-y-6 animate-fadeIn relative" data-id="element-2002">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} data-id="element-2003" />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2004">
        <div data-id="element-2005">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2006">Content Manager</h1>
          <p className="text-sm text-gray-500" data-id="element-2007">
            Create, edit, and manage website content.
          </p>
        </div>
        <button onClick={openCreateModal} className="inline-flex items-center px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 shadow-sm transition-colors" data-id="element-2008">
          <PlusIcon size={16} className="mr-2" data-id="element-2009" />
          Create New{' '}
          {activeTab === 'news' ? 'Article' : activeTab === 'events' ? 'Event' : 'Page'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200" data-id="element-2010">
        {tabsList.map(tab => <button key={tab.id} onClick={() => {
        setActiveTab(tab.id);
        setCurrentPage(1);
      }} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2011">
            {tab.label}
          </button>)}
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center" data-id="element-2012">
        <div className="relative w-full sm:w-96" data-id="element-2013">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-2014" />
          <input type="text" placeholder={`Search ${activeTab}...`} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-2015" />
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto items-center" data-id="element-2016">
          {selectedItems.length > 0 && <div className="flex items-center gap-2 mr-2" data-id="element-2017">
              <span className="text-sm text-gray-500" data-id="element-2018">
                {selectedItems.length} selected
              </span>
              <select className="px-3 py-1.5 border border-gray-300 rounded-md text-sm outline-none" onChange={e => {
            if (e.target.value) {
              handleBulkAction(e.target.value as any);
              e.target.value = '';
            }
          }} data-id="element-2019">
                <option value="" data-id="element-2020">Bulk Actions...</option>
                <option value="publish" data-id="element-2021">Publish Selected</option>
                <option value="draft" data-id="element-2022">Draft Selected</option>
                <option value="delete" data-id="element-2023">Delete Selected</option>
              </select>
            </div>}
          <select value={categoryFilter} onChange={e => {
          setCategoryFilter(e.target.value);
          setCurrentPage(1);
        }} className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" data-id="element-2024">
            <option value="all" data-id="element-2025">All Categories</option>
            <option value="academics" data-id="element-2026">Academics</option>
            <option value="announcements" data-id="element-2027">Announcements</option>
            <option value="events" data-id="element-2028">Events</option>
            <option value="achievements" data-id="element-2029">Achievements</option>
            <option value="education" data-id="element-2030">Education</option>
          </select>
        </div>
      </div>

      {/* Content Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-2031">
        <div className="overflow-x-auto" data-id="element-2032">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2033">
            <thead className="bg-gray-50" data-id="element-2034">
              <tr data-id="element-2035">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-2036">
                  <input type="checkbox" checked={selectedItems.length === paginatedArticles.length && paginatedArticles.length > 0} onChange={handleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2037" />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2038">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2039">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2040">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2041">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2042">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2043">
              {paginatedArticles.length === 0 ? <tr data-id="element-2044">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-2045">
                    No items found matching your criteria.
                  </td>
                </tr> : paginatedArticles.map(article => <tr key={article.id} className="hover:bg-gray-50 transition-colors" data-id="element-2046">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2047">
                      <input type="checkbox" checked={selectedItems.includes(article.id)} onChange={() => handleSelectItem(article.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2048" />
                    </td>
                    <td className="px-6 py-4" data-id="element-2049">
                      <div className="flex items-center" data-id="element-2050">
                        <div className="h-10 w-10 flex-shrink-0 rounded-md overflow-hidden bg-gray-100" data-id="element-2051">
                          {article.image ? <img src={article.image} alt="" className="h-full w-full object-cover" data-id="element-2052" /> : <div className="h-full w-full flex items-center justify-center bg-gray-200 text-gray-400" data-id="element-2053">
                              <EyeIcon size={16} data-id="element-2054" />
                            </div>}
                        </div>
                        <div className="ml-4" data-id="element-2055">
                          <div className="text-sm font-medium text-gray-900 line-clamp-1 cursor-pointer hover:text-blue-600" onClick={() => openEditModal(article)} data-id="element-2056">
                            {article.title}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2057">
                            {article.author}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2058">
                      <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 capitalize" data-id="element-2059">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2060">
                      {new Date(article.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2061">
                      <button onClick={() => handleStatusToggle(article.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 ${article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2062">
                        <span className={`w-1.5 h-1.5 rounded-full ${article.status === 'published' ? 'bg-green-600' : 'bg-gray-500'}`} data-id="element-2063"></span>
                        {article.status === 'published' ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2064">
                      <div className="flex items-center justify-end gap-2" data-id="element-2065">
                        <button onClick={() => openPreviewModal(article)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Preview" data-id="element-2066">
                          <EyeIcon size={18} data-id="element-2067" />
                        </button>
                        <button onClick={() => openEditModal(article)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Edit" data-id="element-2068">
                          <EditIcon size={18} data-id="element-2069" />
                        </button>
                        <button onClick={() => openDeleteModal(article)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" data-id="element-2070">
                          <TrashIcon size={18} data-id="element-2071" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredArticles.length > 0 && <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6" data-id="element-2072">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-2073">
              <div data-id="element-2074">
                <p className="text-sm text-gray-700" data-id="element-2075">
                  Showing{' '}
                  <span className="font-medium" data-id="element-2076">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium" data-id="element-2077">
                    {Math.min(currentPage * itemsPerPage, filteredArticles.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium" data-id="element-2078">{filteredArticles.length}</span>{' '}
                  results
                </p>
              </div>
              <div data-id="element-2079">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-2080">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-2081">
                    Previous
                  </button>

                  {Array.from({
                length: totalPages
              }).map((_, i) => <button key={i} onClick={() => setCurrentPage(i + 1)} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${currentPage === i + 1 ? 'bg-blue-50 text-blue-600 z-10' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-2082">
                      {i + 1}
                    </button>)}

                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-2083">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-2084">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-2085">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-2086">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2087">
                {formData.id ? 'Edit' : 'Create New'}{' '}
                {activeTab === 'news' ? 'Article' : activeTab === 'events' ? 'Event' : 'Page'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2088">
                <XIcon size={24} data-id="element-2089" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-id="element-2090">
              <form id="content-form" onSubmit={handleFormSubmit} className="space-y-6" data-id="element-2091">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2092">
                  <div className="space-y-4 md:col-span-2" data-id="element-2093">
                    <div data-id="element-2094">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2095">
                        Title *
                      </label>
                      <input type="text" required value={formData.title} onChange={e => {
                    setFormData({
                      ...formData,
                      title: e.target.value,
                      slug: !formData.id ? e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : formData.slug
                    });
                  }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Enter title" data-id="element-2096" />
                    </div>
                    <div data-id="element-2097">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2098">
                        Slug URL
                      </label>
                      <input type="text" value={formData.slug} onChange={e => setFormData({
                    ...formData,
                    slug: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50" placeholder="auto-generated-from-title" data-id="element-2099" />
                    </div>
                  </div>

                  <div data-id="element-2100">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2101">
                      Category
                    </label>
                    <select value={formData.category} onChange={e => setFormData({
                  ...formData,
                  category: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2102">
                      <option value="academics" data-id="element-2103">Academics</option>
                      <option value="announcements" data-id="element-2104">Announcements</option>
                      <option value="events" data-id="element-2105">Events</option>
                      <option value="achievements" data-id="element-2106">Achievements</option>
                      <option value="education" data-id="element-2107">Education</option>
                    </select>
                  </div>

                  <div data-id="element-2108">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2109">
                      Publish Date
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2110" />
                  </div>

                  <div className="md:col-span-2" data-id="element-2111">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2112">
                      Featured Image URL
                    </label>
                    <input type="text" value={formData.image} onChange={e => setFormData({
                  ...formData,
                  image: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="https://example.com/image.jpg" data-id="element-2113" />
                  </div>

                  <div className="md:col-span-2" data-id="element-2114">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2115">
                      Excerpt
                    </label>
                    <textarea rows={2} value={formData.excerpt} onChange={e => setFormData({
                  ...formData,
                  excerpt: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" placeholder="Brief summary for cards and lists..." data-id="element-2116"></textarea>
                  </div>

                  <div className="md:col-span-2" data-id="element-2117">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2118">
                      Content *
                    </label>
                    <textarea rows={8} required value={formData.content} onChange={e => setFormData({
                  ...formData,
                  content: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none font-mono text-sm" placeholder="Write your content here (HTML/Markdown supported)..." data-id="element-2119"></textarea>
                  </div>

                  <div data-id="element-2120">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2121">
                      Tags (comma separated)
                    </label>
                    <input type="text" value={formData.tags} onChange={e => setFormData({
                  ...formData,
                  tags: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Education, Event, Important" data-id="element-2122" />
                  </div>

                  <div data-id="element-2123">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2124">
                      Author
                    </label>
                    <input type="text" value={formData.author} onChange={e => setFormData({
                  ...formData,
                  author: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2125" />
                  </div>

                  <div className="md:col-span-2 flex items-center gap-6 pt-2 border-t border-gray-100" data-id="element-2126">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2127">
                      <input type="checkbox" checked={formData.featured} onChange={e => setFormData({
                    ...formData,
                    featured: e.target.checked
                  })} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-2128" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2129">
                        Featured Post
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2130">
                      <input type="checkbox" checked={formData.status === 'published'} onChange={e => setFormData({
                    ...formData,
                    status: e.target.checked ? 'published' : 'draft'
                  })} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-2131" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2132">
                        Publish Immediately
                      </span>
                    </label>
                  </div>
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-2133">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-2134">
                Cancel
              </button>
              <button form="content-form" type="submit" className="px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors" data-id="element-2135">
                {formData.id ? 'Save Changes' : 'Create Content'}
              </button>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-2136">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-2137">
            <div className="p-6 text-center" data-id="element-2138">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2139">
                <TrashIcon size={24} className="text-red-600" data-id="element-2140" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2141">
                Delete Content?
              </h2>
              <p className="text-gray-500 mb-6" data-id="element-2142">
                Are you sure you want to delete "{currentArticle?.title}"? This
                action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center" data-id="element-2143">
                <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-1" data-id="element-2144">
                  Cancel
                </button>
                <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex-1" data-id="element-2145">
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Preview Modal */}
      {isPreviewModalOpen && currentArticle && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-2146">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-2147">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50" data-id="element-2148">
              <div className="flex items-center gap-3" data-id="element-2149">
                <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 capitalize" data-id="element-2150">
                  {currentArticle.category}
                </span>
                <span className="text-sm text-gray-500" data-id="element-2151">Preview Mode</span>
              </div>
              <button onClick={() => setIsPreviewModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2152">
                <XIcon size={24} data-id="element-2153" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8" data-id="element-2154">
              <div className="max-w-3xl mx-auto" data-id="element-2155">
                <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2156">
                  {currentArticle.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-8" data-id="element-2157">
                  <span data-id="element-2158">By {currentArticle.author}</span>
                  <span data-id="element-2159">•</span>
                  <span data-id="element-2160">
                    {new Date(currentArticle.date).toLocaleDateString()}
                  </span>
                </div>

                {currentArticle.image && <img src={currentArticle.image} alt={currentArticle.title} className="w-full h-auto rounded-xl mb-8 object-cover max-h-96" data-id="element-2161" />}

                <div className="prose max-w-none" data-id="element-2162">
                  <p className="text-xl text-gray-600 mb-6 font-medium leading-relaxed" data-id="element-2163">
                    {currentArticle.excerpt}
                  </p>
                  <div className="text-gray-800 whitespace-pre-wrap" data-id="element-2164">
                    {currentArticle.content}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default ContentManager;